var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-1418 — the heading preview, composed by the backend from the rows the save sends.
 *
 * Until CZNA-1418 the form composed the heading itself, in a TypeScript copy of
 * `HeadingComposer` (`utils/headingPreview.ts`, driven by a `headingPreview` recipe in
 * every form-config). A copy drifts: it kept one value per subfield code in each portion,
 * so `Smetana, Bedřich, 1824-1884. Má vlast. $p Vltava $p Šárka` previewed without
 * `Šárka` and was stored with it. Now there is one composer and the preview asks it.
 *
 * <b>What is sent.</b> `genericFormToProposalData(config, formData)` — the exact call both
 * saves make (`ProposalForm#runSave`, `RecordEdit#saveWithPreflight`) — so the composer
 * sees the rows the save would send, order and carried repeats included. The backend runs
 * the save's own sanitizer before composing.
 *
 * <b>Typing never waits for it.</b> Subscribed through `watch(callback)` (no re-render per
 * keystroke, the `MarcPreviewPanel` pattern), debounced, and asynchronous: the field the
 * cataloguer types into is not touched by any of this. While a request is in flight the
 * previous heading stays on screen.
 *
 * <b>A stale answer never wins.</b> Each request aborts the one before it and carries a
 * sequence number; a response whose number is not the latest is dropped even if the abort
 * came too late to stop it.
 *
 * <b>Failure keeps the last value.</b> A preview must not surface an error of its own — the
 * save reports what is wrong with the record.
 *
 * <b>Authority types only.</b> `VocabularyEdit` reuses the form with a vocabulary code the
 * backend's `RecordType` does not know; nothing is composed for it, which is what the old
 * preview did too (the vocabulary config never had a recipe).
 *
 * `ky` is held in a ref and kept out of the effect's dependencies (project MEMORY:
 * `useOkapiKy()` returns a new instance per render).
 */
import { useEffect, useRef, useState } from 'react';
import { useOkapiKy } from '@folio/stripes/core';
import { composeHeading } from '../api/validationService';
import { genericFormToProposalData } from '../components/ProposalForm/utils/genericFormToProposalData';
import { RecordType as ApiRecordType } from '../types/api';
/** Same cadence as the MARC preview beside it — the two move together. */
export var HEADING_PREVIEW_DEBOUNCE_MS = 200;
var AUTHORITY_TYPES = new Set(Object.values(ApiRecordType));
var nameFromRows = function (rows, fieldTag, code) {
    var _a;
    if (!fieldTag || !code)
        return undefined;
    return (_a = rows.find(function (row) { return row.field_tag === fieldTag && row.subfield_code === code; })) === null || _a === void 0 ? void 0 : _a.value;
};
export var useHeadingPreview = function (config, recordType, watch) {
    var ky = useOkapiKy();
    var kyRef = useRef(ky);
    kyRef.current = ky;
    var composable = AUTHORITY_TYPES.has(recordType);
    var _a = useState({ heading: '', settled: !composable }), preview = _a[0], setPreview = _a[1];
    useEffect(function () {
        var _a;
        if (!composable)
            return undefined;
        var seq = 0;
        var controller;
        var timer;
        var nameCode = (_a = config.headingPreview) === null || _a === void 0 ? void 0 : _a.similarNameSubfield;
        var compose = function (formData) {
            seq += 1;
            var mine = seq;
            controller === null || controller === void 0 ? void 0 : controller.abort();
            // A form opened without registered defaults has no `fields` key yet (see
            // `VocabularyEdit`); nothing to compose, and nothing worth a request either.
            var rows = (formData === null || formData === void 0 ? void 0 : formData.fields) ? genericFormToProposalData(config, formData) : [];
            if (rows.length === 0) {
                setPreview({ heading: '', settled: true });
                return;
            }
            controller = new AbortController();
            composeHeading(kyRef.current, { recordType: recordType, proposalData: rows }, controller.signal)
                .then(function (composed) {
                var _a, _b;
                if (mine !== seq)
                    return;
                setPreview({
                    heading: ((_a = composed.headingValue) !== null && _a !== void 0 ? _a : '') + ((_b = composed.headingRemainder) !== null && _b !== void 0 ? _b : ''),
                    nameValue: nameFromRows(rows, composed.fieldTag, nameCode),
                    settled: true,
                });
            })
                .catch(function () {
                if (mine !== seq)
                    return;
                setPreview(function (prev) { return (prev.settled ? prev : __assign(__assign({}, prev), { settled: true })); });
            });
        };
        // `watch(callback)` fires on changes only, so the opened record is composed here.
        compose(watch());
        var subscription = watch(function (formData) {
            clearTimeout(timer);
            timer = setTimeout(function () { return compose(formData); }, HEADING_PREVIEW_DEBOUNCE_MS);
        });
        return function () {
            seq += 1;
            clearTimeout(timer);
            controller === null || controller === void 0 ? void 0 : controller.abort();
            subscription.unsubscribe();
        };
    }, [composable, config, recordType, watch]);
    return preview;
};
